import { type AppDescription, getScreenProperty, type TabDescription } from "@glide/app-description";
import { getAppTabs } from "@glide/common-core/dist/js/components/SerializedApp";
import { defined } from "@glideapps/ts-necessities";
import { logError, truthify, deepEqual } from "@glide/support";
import { isChatTab, isShoppingCartTab } from "@glide/generator/dist/js/screens";
import { ErrorInfo, type ErrorOr } from "@glide/error-info";

// `ErrorInfo` means the precondition was not met, `undefined` means the tab
// was not found.
export function removeTab(
    app: AppDescription,
    screenName: string,
    before?: TabDescription
): ErrorOr<{ newAppDescription: AppDescription; removedTab: TabDescription; index: number } | undefined> {
    const index = getAppTabs(app).findIndex(t => getScreenProperty(t.screenName) === screenName);
    if (index < 0) return undefined;

    const oldTabs = getAppTabs(app);
    const toRemove = defined(oldTabs[index]);

    if (before !== undefined && !deepEqual(toRemove, before, { strict: true })) {
        return new ErrorInfo(412, "Tab has changed");
    }

    if (isShoppingCartTab(toRemove) || isChatTab(toRemove)) {
        logError("Removing special tabs is not allowed.");
        return new ErrorInfo(400, "Removing special tabs is not allowed");
    }

    const newTabs = [...oldTabs.slice(0, index), ...oldTabs.slice(index + 1)];

    // We have to treat Flyout tabs as "hidden" for the purposes
    // of fixing the navigation model. If we don't, then deleting
    // a Tab Bar tab could replace it with a Flyout tab that removes
    // the Tab Bar and makes those tabs permanently inaccessible
    // (until page refresh).
    let visibleIndexes = newTabs.map((t, i) => (t.hidden || truthify(t.inFlyout) ? -1 : i)).filter(i => i >= 0);

    // It is technically possible to remove the singular non-flyout tab.
    // When this happens, we need to bump the first flyout tab out.
    if (visibleIndexes.length === 0 && newTabs.length > 0) {
        let firstTabIndex = newTabs.findIndex(t => !t.hidden);
        if (firstTabIndex < 0) {
            // If we don't even have a tab in the flyout, all tabs are
            // hidden, so we unhide the first tab.
            firstTabIndex = 0;
        }
        newTabs[firstTabIndex] = {
            ...defined(newTabs[firstTabIndex]),
            inFlyout: false,
            hidden: false,
        };
        visibleIndexes = [firstTabIndex];
    }

    return { newAppDescription: { ...app, tabs: newTabs }, removedTab: toRemove, index };
}
